import { PageContainer, PageHeader } from '@/components/Atoms'
import { useAgreementSummary } from '@/hooks/queries/crt/useAgreement'
import { useMasterAgreementMappingsLastUpdated } from '@/hooks/queries/crt/useAgreementMappings'
import { useBudgetAllocationSummary } from '@/hooks/queries/crt/useBudgetAllocations'
import { useLatestCalculationRunAccrual } from '@/hooks/queries/crt/useCalculationRunAccruals'
import { useLatestDistributionMarginMix } from '@/hooks/queries/crt/useDistributionMarginMix'
import { useLatestIpAccrual } from '@/hooks/queries/crt/useIpAccruals'
import { useManualInvestmentsLastUpdated } from '@/hooks/queries/crt/useManualInvestments'
import { useLatestRapForecast } from '@/hooks/queries/crt/useRapForecasts'
import { useRetailerMarginsFmcLastUpdated } from '@/hooks/queries/crt/useRetailerMarginsFmc'
import { useRetailerMarginsNcLastUpdated } from '@/hooks/queries/crt/useRetailerMarginsNc'
import { useLatestSalesDealActuals } from '@/hooks/queries/crt/useSalesDealActuals'
import { useWholesalerMarginsFmcLastUpdated } from '@/hooks/queries/crt/useWholesalerMarginsFmc'
import { useWholesalerMarginsNcLastUpdated } from '@/hooks/queries/crt/useWholesalerMarginsNc'
import {
  toCrtPeriodState,
  toMonthlyImportPeriodState,
  toUpdatedAt,
} from '@/lib/utils/getLastUpdatedState'
import {
  DataSources,
  type DataSourceItem,
} from '@/routes/_auth/_modules/_shared/-components/DataSources'
import { crtPeriodToParams } from '@/lib/utils/crtPeriodParams'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { getDefaultImportPeriod } from '@/components/CreateCrtPeriodDetailsDialog'
import { createFileRoute } from '@tanstack/react-router'
import { startOfYear } from 'date-fns'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_auth/_modules/crt/customer-investments/data-sources/')({
  component: CustomerInvestmentsDataSources,
})

function CustomerInvestmentsDataSources() {
  const { t } = useTranslation(['crt', 'common'])

  // The monthly actuals pages are addressed by period, and the current month is never actualised yet:
  // the data source opens the previous month's page, the import the current MOPE expects.
  const expectedPeriodParams = crtPeriodToParams(formatDateOnly(getDefaultImportPeriod()))

  const { data: agreementSummary } = useAgreementSummary()
  const { data: latestRapForecast } = useLatestRapForecast()
  const { data: latestSalesDealActuals } = useLatestSalesDealActuals()
  const { data: latestDistributionMarginMix } = useLatestDistributionMarginMix()
  const { data: latestIpAccrual } = useLatestIpAccrual()
  const { data: latestCalculationRunAccrual } = useLatestCalculationRunAccrual()
  const { data: budgetAllocationSummary } = useBudgetAllocationSummary()
  const { data: retailerMarginsFmcLastUpdated } = useRetailerMarginsFmcLastUpdated()
  const { data: retailerMarginsNcLastUpdated } = useRetailerMarginsNcLastUpdated()
  const { data: wholesalerMarginsFmcLastUpdated } = useWholesalerMarginsFmcLastUpdated()
  const { data: wholesalerMarginsNcLastUpdated } = useWholesalerMarginsNcLastUpdated()
  const { data: masterAgreementMappingsLastUpdated } = useMasterAgreementMappingsLastUpdated()
  const { data: manualInvestmentsLastUpdated } = useManualInvestmentsLastUpdated()

  const automatedImports: DataSourceItem[] = [
    {
      name: t('route.crt.agreements', { ns: 'common' }),
      frequency: t('dataSources.frequency.monthly'),
      frequencySchedule: { type: 'monthly', dayOfMonth: 5 },
      frequencyTooltip: t('customerInvestmentsDataSources.agreements.frequencyDetail'),
      updatedAt: toUpdatedAt(agreementSummary),
      description: t('customerInvestmentsDataSources.agreements.linkText'),
      link: { to: '/crt/customer-investments/data-sources/agreements' },
      permissions: ['crt_agreements_view'],
    },
    {
      name: t('route.crt.rapForecasts', { ns: 'common' }),
      frequency: t('dataSources.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsDataSources.rapForecasts.frequencyDetail'),
      crtPeriodState: toCrtPeriodState(latestRapForecast),
      description: t('customerInvestmentsDataSources.rapForecasts.linkText'),
      link: latestRapForecast?.id
        ? {
            to: '/crt/customer-investments/data-sources/rap-forecasts/$year/$month',
            params: crtPeriodToParams(latestRapForecast.crtPeriod),
          }
        : { to: '/crt/customer-investments/data-sources/rap-forecasts' },
      permissions: ['crt_rap_forecasts_view'],
    },
    {
      name: t('route.crt.salesDealActuals', { ns: 'common' }),
      frequency: t('dataSources.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsDataSources.salesDealActuals.frequencyDetail'),
      crtPeriodState: toMonthlyImportPeriodState(latestSalesDealActuals),
      description: t('customerInvestmentsDataSources.salesDealActuals.linkText'),
      link: {
        to: '/crt/customer-investments/data-sources/sales-deal-actuals/$year/$month',
        params: expectedPeriodParams,
      },
      permissions: ['crt_sales_deal_actuals_view'],
    },
    {
      name: t('route.crt.distributionMarginMix', { ns: 'common' }),
      frequency: t('dataSources.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsDataSources.distributionMarginMix.frequencyDetail'),
      crtPeriodState: toCrtPeriodState(latestDistributionMarginMix),
      description: t('customerInvestmentsDataSources.distributionMarginMix.linkText'),
      link: latestDistributionMarginMix?.id
        ? {
            to: '/crt/customer-investments/data-sources/distribution-margin-mix/$year/$month',
            params: crtPeriodToParams(latestDistributionMarginMix.crtPeriod),
          }
        : { to: '/crt/customer-investments/data-sources/distribution-margin-mix' },
      permissions: ['crt_distribution_margin_mix_view'],
    },
    {
      name: t('route.crt.ipAccruals', { ns: 'common' }),
      frequency: t('dataSources.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsDataSources.ipAccruals.frequencyDetail'),
      crtPeriodState: toMonthlyImportPeriodState(latestIpAccrual),
      description: t('customerInvestmentsDataSources.ipAccruals.linkText'),
      link: {
        to: '/crt/customer-investments/data-sources/ip-accruals/$year/$month',
        params: expectedPeriodParams,
      },
      permissions: ['crt_ip_accruals_view'],
    },
    {
      name: t('route.crt.calculationRunAccruals', { ns: 'common' }),
      frequency: t('dataSources.frequency.monthly'),
      frequencyTooltip: t('customerInvestmentsDataSources.calculationRunAccruals.frequencyDetail'),
      crtPeriodState: toMonthlyImportPeriodState(latestCalculationRunAccrual),
      description: t('customerInvestmentsDataSources.calculationRunAccruals.linkText'),
      link: {
        to: '/crt/customer-investments/data-sources/calculation-run-accruals/$year/$month',
        params: expectedPeriodParams,
      },
      permissions: ['crt_calculation_run_accruals_view'],
    },
    {
      name: t('route.crt.budgetAllocations', { ns: 'common' }),
      frequency: t('dataSources.frequency.daily'),
      frequencySchedule: { type: 'daily' },
      updatedAt: toUpdatedAt(budgetAllocationSummary),
      description: t('customerInvestmentsDataSources.budgetAllocations.linkText'),
      link: { to: '/crt/customer-investments/data-sources/budget-allocations' },
      permissions: ['crt_budget_allocations_view'],
    },
  ]

  const assumptions: DataSourceItem[] = [
    {
      name: t('route.crt.retailerMarginsFmc', { ns: 'common' }),
      description: t('customerInvestmentsDataSources.retailerMarginsFmc.linkText'),
      updatedAt: toUpdatedAt(retailerMarginsFmcLastUpdated),
      link: { to: '/crt/customer-investments/data-sources/retailer-margins/fmc' },
      permissions: ['crt_retailer_margins_fmc_view'],
    },
    {
      name: t('route.crt.retailerMarginsNc', { ns: 'common' }),
      description: t('customerInvestmentsDataSources.retailerMarginsNc.linkText'),
      updatedAt: toUpdatedAt(retailerMarginsNcLastUpdated),
      link: { to: '/crt/customer-investments/data-sources/retailer-margins/nc' },
      permissions: ['crt_retailer_margins_nc_view'],
    },
    {
      name: t('route.crt.wholesalerMarginsFmc', { ns: 'common' }),
      description: t('customerInvestmentsDataSources.wholesalerMarginsFmc.linkText'),
      updatedAt: toUpdatedAt(wholesalerMarginsFmcLastUpdated),
      link: { to: '/crt/customer-investments/data-sources/wholesaler-margins/fmc' },
      permissions: ['crt_wholesaler_margins_fmc_view'],
    },
    {
      name: t('route.crt.wholesalerMarginsNc', { ns: 'common' }),
      description: t('customerInvestmentsDataSources.wholesalerMarginsNc.linkText'),
      updatedAt: toUpdatedAt(wholesalerMarginsNcLastUpdated),
      link: { to: '/crt/customer-investments/data-sources/wholesaler-margins/nc' },
      permissions: ['crt_wholesaler_margins_nc_view'],
    },
    {
      name: t('route.crt.masterAgreements', { ns: 'common' }),
      description: t('customerInvestmentsDataSources.masterAgreements.linkText'),
      updatedAt: toUpdatedAt(masterAgreementMappingsLastUpdated),
      link: {
        to: '/crt/customer-investments/data-sources/master-agreements',
        search: { validFrom: startOfYear(new Date()) },
      },
      permissions: ['crt_agreement_mappings_view'],
    },
    {
      name: t('route.crt.manualInvestments', { ns: 'common' }),
      description: t('customerInvestmentsDataSources.manualInvestments.linkText'),
      updatedAt: toUpdatedAt(manualInvestmentsLastUpdated),
      link: { to: '/crt/customer-investments/data-sources/manual-investments' },
      permissions: ['crt_manual_investments_view'],
    },
  ]

  return (
    <PageContainer>
      <PageHeader>{t('customerInvestmentsDataSources.pageHeader')}</PageHeader>
      <DataSources automatedImports={automatedImports} assumptions={assumptions} />
    </PageContainer>
  )
}
